import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent } from '@/shared/ui/card';
import { Button } from '@/shared/ui/button';
import { Badge } from '@/shared/ui/badge';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/shared/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/shared/ui/alert-dialog';
import { ArrowLeft, UserPlus, PauseCircle, PlayCircle, Trash2 } from 'lucide-react';
import {
  getSousCompte, majSousCompte, creerAdmin, supprimerSousCompte, LIBELLES_ACTION,
} from '@/shared/api/superadmin';
import ModulesEtPlafonds from '@/features/superadmin/components/ModulesEtPlafonds';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/shared/ui/tabs';
import BoutonMotDePasse from '@/shared/components/BoutonMotDePasse';

const dateCourte = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }); }
  catch { return iso; }
};

function Volume({ libelle, valeur, testid }) {
  return (
    <Card>
      <CardContent className="p-4">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{libelle}</p>
        <p className="mt-1 text-2xl font-bold text-slate-900" data-testid={testid}>{valeur}</p>
      </CardContent>
    </Card>
  );
}

export default function SousComptePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [sc, setSc] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [dialogueAdmin, setDialogueAdmin] = useState(false);
  const [confirmation, setConfirmation] = useState(false);
  const [dialogueSuppression, setDialogueSuppression] = useState(false);
  const [erreurSuppression, setErreurSuppression] = useState('');
  const [suppressionEnCours, setSuppressionEnCours] = useState(false);
  const [form, setForm] = useState({ email: '', name: '', password: '' });
  const [erreur, setErreur] = useState('');
  const [envoi, setEnvoi] = useState(false);

  const charger = useCallback(async () => {
    try { setSc(await getSousCompte(id)); } catch (e) { console.error(e); }
    setChargement(false);
  }, [id]);

  useEffect(() => { charger(); }, [charger]);

  const basculerStatut = async () => {
    const cible = sc.status === 'actif' ? 'suspendu' : 'actif';
    setConfirmation(false);
    try { await majSousCompte(id, { status: cible }); await charger(); }
    catch (e) { console.error(e); }
  };

  /**
   * Supprime le sous-compte, ou affiche le refus du serveur tel quel.
   *
   * Deux refus possibles, et les deux comptent : le sous-compte par défaut est protégé
   * (400), et un sous-compte qui porte encore des données l'est aussi (409, avec le
   * détail des volumes). Le message du serveur est repris mot pour mot plutôt que
   * réécrit ici : c'est lui qui connaît la raison, et un texte local finirait par
   * mentir le jour où la règle change.
   */
  const supprimer = async () => {
    setErreurSuppression('');
    setSuppressionEnCours(true);
    try {
      await supprimerSousCompte(id);
      navigate('/superadmin');
    } catch (e) {
      setErreurSuppression(e?.response?.data?.detail || 'La suppression a échoué');
    }
    setSuppressionEnCours(false);
  };

  const ajouterAdmin = async () => {
    setErreur(''); setEnvoi(true);
    try {
      await creerAdmin(id, form);
      setDialogueAdmin(false);
      setForm({ email: '', name: '', password: '' });
      await charger();
    } catch (e) {
      setErreur(e?.response?.data?.detail || "La création a échoué");
    }
    setEnvoi(false);
  };

  if (chargement) return <div className="p-6 text-sm text-slate-500">Chargement…</div>;
  if (!sc) return <div className="p-6 text-sm text-red-600">Sous-compte introuvable.</div>;

  const actif = sc.status === 'actif';
  const motDePasseValide = form.password.length >= 10;
  // Le serveur refuse la suppression d'un sous-compte non vide. Le dire avant le clic
  // vaut mieux qu'un échec après : l'écran connaît déjà les volumes.
  const volumesPortes = [
    ['compte', sc.users_count], ['client', sc.clients_count], ['équipement', sc.assets_count],
  ].filter(([, n]) => n > 0);
  const estVide = volumesPortes.length === 0;

  return (
    <div className="space-y-6 p-4 sm:p-6" data-testid="sous-compte-page">
      <div>
        <Button
          variant="ghost" size="sm" onClick={() => navigate('/superadmin')}
          data-testid="retour-console-btn" className="-ml-2 mb-2 text-slate-500"
        >
          <ArrowLeft className="mr-1 h-4 w-4" /> Sous-comptes
        </Button>
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl"
              data-testid="sous-compte-nom">{sc.name}</h1>
          <Badge
            data-testid="sous-compte-statut"
            className={actif ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100'
                             : 'bg-red-100 text-red-700 hover:bg-red-100'}
          >
            {actif ? 'Actif' : 'Suspendu'}
          </Badge>
        </div>
        <p className="mt-1 text-sm text-slate-500">Créé le {dateCourte(sc.created_at)}</p>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Volume libelle="Comptes" valeur={sc.users_count} testid="volume-comptes" />
        <Volume libelle="Clients" valeur={sc.clients_count} testid="volume-clients" />
        <Volume libelle="Actifs" valeur={sc.assets_count} testid="volume-actifs" />
        <Volume libelle="Mandats" valeur={sc.mandats_count} testid="volume-mandats" />
      </div>

      <ModulesEtPlafonds sousCompte={sc} onEnregistre={charger} />

      {/* Ce que contient le parc, par type. Trois nombres suffisent a savoir de quoi
          on parle : combien d actifs, quelle puissance, combien sont sous mandat. Le
          compte de mandats porte sur les actifs references, un mandat en couvrant
          plusieurs. */}
      {(sc.repartition || []).length > 0 && (
        <section data-testid="repartition-actifs">
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-slate-900">
            Répartition des actifs
          </h2>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {sc.repartition.map((r) => (
              <div key={r.type} className="rounded-lg border border-slate-200 bg-white p-4"
                   data-testid={`repartition-${r.type.toLowerCase()}`}>
                <p className="text-[11px] font-medium uppercase tracking-wider text-slate-400">{r.type}</p>
                <p className="mt-1 text-2xl font-semibold tabular-nums text-slate-900">{r.actifs}</p>
                <p className="text-[12px] text-slate-500">
                  {r.puissance_kw} kW · {r.mandats} sous mandat
                </p>
              </div>
            ))}
          </div>
        </section>
      )}

      <div className="flex flex-wrap gap-2">
        <Button onClick={() => setDialogueAdmin(true)} data-testid="ajouter-admin-btn" size="sm">
          <UserPlus className="mr-1 h-4 w-4" /> Ajouter un administrateur
        </Button>
        <Button
          variant={actif ? 'destructive' : 'default'} size="sm"
          onClick={() => setConfirmation(true)} data-testid="basculer-statut-btn"
        >
          {actif ? <><PauseCircle className="mr-1 h-4 w-4" /> Suspendre</>
                 : <><PlayCircle className="mr-1 h-4 w-4" /> Réactiver</>}
        </Button>
        <Button
          variant="outline" size="sm"
          className="border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800"
          onClick={() => { setErreurSuppression(''); setDialogueSuppression(true); }}
          data-testid="supprimer-sous-compte-btn"
        >
          <Trash2 className="mr-1 h-4 w-4" /> Supprimer
        </Button>
      </div>

      {/* Trois onglets plutot qu une seule table de comptes. Le super admin entre deja
          en observation dans le sous-compte d un clic, ou il voit tout : montrer ici une
          liste reduite n ouvre aucun acces neuf, elle epargne le detour. Les listes sont
          bornees a 200 lignes cote serveur. */}
      <Tabs defaultValue="comptes" className="space-y-3">
        <TabsList data-testid="onglets-sous-compte">
          <TabsTrigger value="comptes" data-testid="onglet-comptes">
            Comptes {sc.comptes.length > 0 && `(${sc.comptes.length})`}
          </TabsTrigger>
          <TabsTrigger value="clients" data-testid="onglet-clients">
            Clients {sc.clients_count > 0 && `(${sc.clients_count})`}
          </TabsTrigger>
          <TabsTrigger value="actifs" data-testid="onglet-actifs">
            Actifs {sc.assets_count > 0 && `(${sc.assets_count})`}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="comptes">
          <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nom</TableHead>
                  <TableHead>Adresse e-mail</TableHead>
                  <TableHead>Rôle</TableHead>
                  <TableHead>Créé le</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sc.comptes.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} className="text-sm text-slate-500">
                      Aucun compte. Personne ne peut se connecter à ce sous-compte.
                    </TableCell>
                  </TableRow>
                )}
                {sc.comptes.map((u) => (
                  <TableRow key={u.id} data-testid={`compte-${u.id}`}>
                    <TableCell className="font-medium text-slate-900">{u.name}</TableCell>
                    <TableCell className="text-[12px] text-slate-600">{u.email}</TableCell>
                    <TableCell><Badge variant="outline">{u.role}</Badge></TableCell>
                    <TableCell className="text-[12px] text-slate-500">{dateCourte(u.created_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="clients">
          <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Client</TableHead>
                  <TableHead>Ville</TableHead>
                  <TableHead>Agrégateur</TableHead>
                  <TableHead>Statut</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(sc.clients || []).length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} className="text-sm text-slate-500">
                      Aucun client dans ce sous-compte.
                    </TableCell>
                  </TableRow>
                )}
                {(sc.clients || []).map((c) => (
                  <TableRow key={c.id} data-testid={`sc-client-${c.id}`}>
                    <TableCell className="font-medium text-slate-900">
                      {`${c.prenom || ''} ${c.nom || ''}`.trim() || '—'}
                    </TableCell>
                    <TableCell className="text-[12px] text-slate-600">
                      {c.ville || '—'}{c.code_postal ? ` (${c.code_postal})` : ''}
                    </TableCell>
                    <TableCell className="text-[12px] text-slate-600">{c.aggregateur || 'Non assigné'}</TableCell>
                    <TableCell><Badge variant="outline">{c.statut || '—'}</Badge></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {sc.clients_count > (sc.clients || []).length && (
            <p className="mt-2 text-[12px] text-slate-500" data-testid="clients-tronques">
              {(sc.clients || []).length} lignes affichées sur {sc.clients_count}. Le détail complet
              se lit en entrant en observation dans le sous-compte.
            </p>
          )}
        </TabsContent>

        <TabsContent value="actifs">
          <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead>Marque / Modèle</TableHead>
                  <TableHead>Puissance</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Connexion</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(sc.actifs || []).length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-sm text-slate-500">
                      Aucun actif dans ce sous-compte.
                    </TableCell>
                  </TableRow>
                )}
                {(sc.actifs || []).map((a) => (
                  <TableRow key={a.id} data-testid={`sc-actif-${a.id}`}>
                    <TableCell><Badge variant="outline">{a.type || '—'}</Badge></TableCell>
                    <TableCell className="text-[12px] text-slate-600">
                      <span className="font-medium text-slate-900">{a.marque || '—'}</span>
                      {a.modele ? ` ${a.modele}` : ''}
                    </TableCell>
                    <TableCell className="text-[12px] tabular-nums text-slate-600">
                      {a.puissance_kw ? `${a.puissance_kw} kW` : '—'}
                    </TableCell>
                    <TableCell className="text-[12px] text-slate-600">{a.client_nom || '—'}</TableCell>
                    <TableCell className="text-[12px] text-slate-500">{a.statut_connexion || 'inconnu'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {sc.assets_count > (sc.actifs || []).length && (
            <p className="mt-2 text-[12px] text-slate-500" data-testid="actifs-tronques">
              {(sc.actifs || []).length} lignes affichées sur {sc.assets_count}.
            </p>
          )}
        </TabsContent>
      </Tabs>

      <section>
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-slate-900">
          Journal de ce sous-compte
        </h2>
        {sc.journal.length === 0 ? (
          <p className="text-sm text-slate-500" data-testid="journal-vide">
            Aucune action enregistrée.
          </p>
        ) : (
          <ul className="space-y-1" data-testid="journal-sous-compte">
            {sc.journal.map((l) => (
              <li key={l.id} className="flex flex-wrap items-baseline gap-x-3 rounded border border-slate-200 bg-white px-3 py-2 text-[12px]">
                <span className="font-medium text-slate-900">
                  {LIBELLES_ACTION[l.action] || l.action}
                </span>
                <span className="text-slate-500">{l.acteur_nom || l.acteur_email}</span>
                <span className="ml-auto text-slate-400">{dateCourte(l.created_at)}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <Dialog open={dialogueAdmin} onOpenChange={setDialogueAdmin}>
        <DialogContent data-testid="dialog-ajouter-admin">
          <DialogHeader>
            <DialogTitle>Ajouter un administrateur</DialogTitle>
            <DialogDescription>
              Il pourra tout faire à l&apos;intérieur de ce sous-compte, et rien en dehors.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="admin-nom">Nom</Label>
              <Input id="admin-nom" value={form.name} data-testid="input-admin-nom"
                     onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="admin-email">Adresse e-mail</Label>
              <Input id="admin-email" type="email" value={form.email} data-testid="input-admin-email"
                     onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="admin-mdp">Mot de passe</Label>
              {/* Le champ reste en clair une fois genere : le mot de passe se
                  transmet de vive voix, et un champ masque obligerait a le retaper
                  pour le lire. Il ne part jamais par courriel. */}
              <Input id="admin-mdp" type={form.password ? 'text' : 'password'}
                     value={form.password} data-testid="input-admin-mdp"
                     onChange={(e) => setForm({ ...form, password: e.target.value })} />
              <BoutonMotDePasse valeur={form.password} tid="sa-souscompte-mdp"
                                onGenere={(m) => setForm({ ...form, password: m })} />
              <p className={`text-[11px] ${motDePasseValide ? 'text-slate-500' : 'text-amber-600'}`}>
                10 caractères minimum.
              </p>
            </div>
            {erreur && <p className="text-sm text-red-600" data-testid="erreur-admin">{erreur}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogueAdmin(false)}>Annuler</Button>
            <Button
              onClick={ajouterAdmin} data-testid="valider-admin-btn"
              disabled={envoi || !form.email || form.name.length < 2 || !motDePasseValide}
            >
              {envoi ? 'Création…' : 'Créer le compte'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmation} onOpenChange={setConfirmation}>
        <AlertDialogContent data-testid="dialog-confirmation-statut">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {actif ? `Suspendre ${sc.name} ?` : `Réactiver ${sc.name} ?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {actif
                ? `Les ${sc.users_count} compte(s) de ce sous-compte seront immédiatement bloqués à la connexion. Les données ne sont pas touchées, et la réactivation rend l'accès aussitôt.`
                : "Les comptes de ce sous-compte pourront de nouveau se connecter."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="annuler-statut-btn">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={basculerStatut} data-testid="confirmer-statut-btn">
              {actif ? 'Suspendre' : 'Réactiver'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={dialogueSuppression} onOpenChange={setDialogueSuppression}>
        <AlertDialogContent data-testid="dialog-confirmation-suppression">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer {sc.name} ?</AlertDialogTitle>
            <AlertDialogDescription>
              {estVide
                ? "Le sous-compte disparaît de la console. L'opération est définitive et sera inscrite au journal de plateforme."
                : `Ce sous-compte porte encore ${volumesPortes.map(([mot, n]) => `${n} ${mot}${n > 1 ? 's' : ''}`).join(', ')}. Le serveur refusera la suppression tant que ces données existent : la suspension est ce qui coupe l'accès sans rien détruire.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {erreurSuppression && (
            <p className="text-sm text-red-600" data-testid="erreur-suppression">{erreurSuppression}</p>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="annuler-suppression-btn">Annuler</AlertDialogCancel>
            {/* Le bouton reste cliquable même sur un sous-compte non vide : c'est le
                serveur qui tranche, et son refus est plus fiable que ce compte de
                volumes, qui date du dernier chargement de la page. */}
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); supprimer(); }}
              disabled={suppressionEnCours}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirmer-suppression-btn"
            >
              {suppressionEnCours ? 'Suppression…' : 'Supprimer définitivement'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
